import { useMemo } from "react"
import { DashBoardConditions } from "features/dashBoard/types"
import { CommonGraphStyle, selectedItemStyle, filter } from "../../common"
import { useSunburstGraphOption } from "./options"
import { round } from "shared/util"
import { DownLoadData } from "components/download/csv/types"

export type SunburstNode = {
  code: string
  name: string
  value: number
  children?: SunburstNode[]
  itemStyle?: CommonGraphStyle
}

type useSunburstGraphModelProps<T> = {
	dataList: T[] | null
	conditions: DashBoardConditions,
	filterText: string | undefined
	graphOption: ReturnType<typeof useSunburstGraphOption<T>>
}
export const useSunburstGraphModel = <T,>({
	dataList,
	conditions,
	filterText,
	graphOption: {
		configure, targetList,
    axisDef, valueDef
  }
}: useSunburstGraphModelProps<T>) => useMemo(
	() => {
		if (!dataList || dataList.length === 0) return null
		if (!axisDef?.key || !valueDef?.key) return null

		const condition = conditions[axisDef.key]
		const renderData: SunburstNode[] = []
		for (const record of dataList) {
			const axis = axisDef.val(record)
			if (!axis) continue
			const value = valueDef.val(record)
			if (value === undefined || value === 0) continue
			if (!filter(record, configure, conditions, axisDef.key, targetList)) continue

			const axisList = Array.isArray(axis) ? axis : [axis]
			for (const ax of axisList) {
				if (filterText && ax.name) {
					const axName = ax.name
					if (filterText.split(' ').every((t) => axName.indexOf(t) < 0)) continue
				}
				const itemStyle = selectedItemStyle(condition, ax)
				if (ax.category) {
					const category = ax.category
					let itc = renderData.find((r) => r.code === category.code)
					if (!itc) {
						itc = {
							code: category.code,
							name: category.name ?? '',
							value: 0,
							itemStyle
						}
						renderData.push(itc)
					}
					itc.value += value
					if (!itc.children) itc.children = []

					let it = itc.children.find((r) => r.code === ax.code)
					if (!it) {
						it = {
							code: ax.code,
							name: ax.name ?? '',
							value: 0,
							itemStyle
						}
						itc.children.push(it)
					}
					it.value += value
				} else {
					let it = renderData.find((r) => r.code === ax.code)
					if (!it) {
						it = {
							code: ax.code,
							name: ax.name ?? '',
							value: 0,
							itemStyle
						}
						renderData.push(it)
					}
					it.value += value
				}
			}
		}

		const indexData: SunburstNode[] = []
		renderData.sort((a, b) => b.value - a.value)
		renderData.forEach((it) => {
			it.value = round(it.value)
			indexData.push(it)
			if (it.children) {
				it.children?.sort((a, b) => b.value - a.value)
				it.children.forEach((child) => {
					indexData.push(child)
					child.value = round(child.value)
				})
			}
		})
		return {
			renderData,
			indexData,
			csvBuilder: (): DownLoadData | undefined => {
				if (!axisDef?.key || !valueDef?.key) return undefined
				const axisKey = axisDef.key
				const valueKey = valueDef.key
				return {
					header: [{
						title: axisDef.title,
						key: axisDef.key
					}, {
						title: valueDef.title,
						key: valueDef.key,
						unit: valueDef.unit
					}],
					body: renderData.map((data) => ({
						[axisKey]: data.name,
						[valueKey]: data.value
					}))
				}
			}
		}
	},
	[dataList, axisDef, valueDef, filterText, conditions, configure, targetList]
)
